import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MapPin, Instagram, Facebook, Twitter, Tag, Heart } from "lucide-react";
import { NAV_LINKS, SUBNAV, RANGER_AVATAR } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function Logo() {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
      <div className="w-9 h-9 flex items-center justify-center border border-[#FF0055] overflow-hidden">
        <img src={RANGER_AVATAR} alt="Visit RiNo" className="w-full h-full object-cover" />
      </div>
      <div className="leading-none">
        <span className="font-display font-extrabold text-lg tracking-tight text-white">VISIT<span className="text-[#FF0055]">RiNo</span></span>
        <span className="block font-accent text-[9px] text-[#00E5FF] tracking-[0.25em]">River North Art District</span>
      </div>
    </Link>
  );
}

export function Navbar() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const { user } = useAuth();
  const isCustomer = user && user.role === "customer";
  return (
    <header className="sticky top-0 z-50 glass border-b border-white/10">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Logo />
        <div className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium link-underline transition-colors ${isActive || loc.pathname.startsWith(l.to) ? "text-[#FF0055]" : "text-neutral-300 hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Link to={isCustomer ? "/account" : "/account/login"} data-testid="nav-account" title="My RiNo" className="hidden sm:flex items-center gap-1.5 text-sm text-neutral-300 hover:text-[#FF0055] transition-colors">
            <Heart size={16} /> {isCustomer ? "My RiNo" : "Sign In"}
          </Link>
          <Link to="/join" className="hidden md:block text-sm text-neutral-300 hover:text-white link-underline" data-testid="nav-business-portal">For Business</Link>
          <Link to="/owner" className="hidden sm:block">
            <Button data-testid="nav-join-btn" className="bg-[#FF0055] hover:bg-[#ff2d74] text-white rounded-none font-semibold h-9 px-5 hover:shadow-[0_0_18px_rgba(255,0,85,0.55)] transition-shadow">
              Join RiNo
            </Button>
          </Link>
          <button data-testid="nav-menu-toggle" className="lg:hidden text-white" onClick={() => setOpen(!open)} aria-label="Toggle menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>
      {open && (
        <div className="lg:hidden glass border-t border-white/10 px-4 py-4 flex flex-col gap-3">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase()}`} className="text-neutral-200 py-1 text-base">
              {l.label}
            </NavLink>
          ))}
          <div className="border-t border-white/10 my-1 pt-2">
            <span className="font-accent text-[10px] text-[#00E5FF] tracking-[0.2em]">EXPLORE</span>
          </div>
          {SUBNAV.map((s) => (
            <NavLink key={s.label} to={s.to} onClick={() => setOpen(false)}
              data-testid={`mobile-subnav-${s.label.toLowerCase().replace(/\s|&/g, "").replace(/--+/g, "-")}`}
              className={`py-1 text-base ${s.deal ? "text-[#FFEA00] font-bold flex items-center gap-1.5" : "text-neutral-300"}`}>
              {s.deal && <Tag size={15} />}{s.label}
            </NavLink>
          ))}
          <Link to={isCustomer ? "/account" : "/account/login"} onClick={() => setOpen(false)} data-testid="mobile-nav-account" className="text-neutral-200 py-1 text-base flex items-center gap-2"><Heart size={15} /> {isCustomer ? "My RiNo" : "Sign In / Follow"}</Link>
          <Link to="/owner" onClick={() => setOpen(false)}>
            <Button className="w-full bg-[#FF0055] rounded-none">Join RiNo</Button>
          </Link>
        </div>
      )}
      <div className="hidden lg:block border-t border-white/10 bg-gradient-to-r from-[#0d0d0d] via-[#131313] to-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-8 h-14 overflow-x-auto">
          {SUBNAV.map((s) => {
            const testid = `subnav-${s.label.toLowerCase().replace(/\s|&/g, "").replace(/--+/g, "-")}`;
            const active = s.deal ? loc.pathname === "/offers" : (loc.pathname === s.to || loc.pathname.startsWith(s.to + "/"));
            if (s.deal) {
              return (
                <Link key={s.label} to={s.to} data-testid={testid} className="h-full flex items-center flex-shrink-0">
                  <span className="flex items-center gap-1.5 bg-[#FFEA00] text-black font-extrabold text-sm px-4 py-1.5 rounded-full hover:shadow-[0_0_20px_rgba(255,234,0,0.6)] transition-shadow animate-pulse-slow">
                    <Tag size={15} /> {s.label}
                  </span>
                </Link>
              );
            }
            return (
              <Link key={s.label} to={s.to} data-testid={testid}
                className={`h-full flex items-center text-[15px] font-semibold tracking-wide whitespace-nowrap border-b-2 transition-colors ${active ? "text-[#00E5FF] border-[#00E5FF]" : "text-neutral-300 border-transparent hover:text-white"}`}>
                {s.label}
              </Link>
            );
          })}
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  const [email, setEmail] = useState("");
  const subscribe = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/subscribe", { email });
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <footer className="relative border-t border-white/10 bg-[#0A0A0A] mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <span className="font-display font-extrabold text-2xl text-white">VISIT<span className="text-[#FF0055]">RiNo</span></span>
          <p className="text-neutral-400 mt-4 max-w-md leading-relaxed">
            The official destination guide to Denver's River North Art District — murals, breweries, galleries, makers, and the events that make RiNo unforgettable.
          </p>
          <form onSubmit={subscribe} className="mt-6 flex max-w-sm border border-white/15">
            <input
              data-testid="footer-newsletter-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email for RiNo insider news"
              className="flex-1 bg-transparent px-3 py-2.5 text-sm text-white outline-none placeholder:text-neutral-500"
            />
            <button data-testid="footer-newsletter-submit" className="bg-[#FF0055] px-4 text-sm font-semibold text-white hover:bg-[#ff2d74] transition-colors">Subscribe</button>
          </form>
        </div>
        <div>
          <h4 className="font-accent text-[#00E5FF] text-sm mb-4">Explore</h4>
          <ul className="space-y-2 text-neutral-400 text-sm">
            {NAV_LINKS.map((l) => <li key={l.to}><Link to={l.to} className="hover:text-white transition-colors">{l.label}</Link></li>)}
            <li><Link to="/advertise" data-testid="footer-advertise-link" className="text-[#FFEA00] hover:text-white transition-colors font-medium">Advertise With Us</Link></li>
            <li><Link to="/owner" className="hover:text-white transition-colors">Business Login</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-accent text-[#00E5FF] text-sm mb-4">RiNo HQ</h4>
          <p className="text-neutral-400 text-sm flex items-start gap-2"><MapPin size={16} className="mt-0.5 text-[#FF0055]" /> 1320 27th St, Suite G<br/>Denver, CO 80205</p>
          <div className="flex gap-3 mt-4">
            <a href="#" aria-label="Instagram" className="text-neutral-400 hover:text-[#FF0055]"><Instagram size={18} /></a>
            <a href="#" aria-label="Facebook" className="text-neutral-400 hover:text-[#FF0055]"><Facebook size={18} /></a>
            <a href="#" aria-label="Twitter" className="text-neutral-400 hover:text-[#FF0055]"><Twitter size={18} /></a>
          </div>
          <Link to="/admin" className="block mt-6 text-xs text-neutral-600 hover:text-neutral-400" data-testid="footer-admin-link">Admin</Link>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} Visit RiNo. Powered by The RiNo Ranger. All rights reserved.
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="App min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
